import { useEffect, useState, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMediaQuery } from "react-responsive";
import { useQueryClient } from "@tanstack/react-query";

import {
  AssetFormInterface,
  FileModel,
  AssetFileModel,
  AssetModel,
  AssetAttachmentModel,
  AssetFileType,
  AssetType,
  AssetPurchaseType,
  AssetJoint,
} from "@interfaces/index";
import { useCommonStore, useUserStore } from "@stores/index";
import {
  QUERY_WIDTHS,
  ASSET_FILE_EXTENSIONS,
  assetSchema,
  formatAmountAfterEditing,
  formatAmountOnBlur,
  formatPercentOnEdit,
  formatPercentOnBlur,
  getLabelFileAgreementField,
} from "@utils/index";
import { attachmentsService } from "@services/index";
import { useAssetCreate, useAssetUpdate } from "@providers/index";
import { useNotify } from "@hooks/index";
import {
  Drawer,
  DrawerHeader,
  Form,
  FormRow,
  DatePicker,
  FormInput,
  FormCheckbox,
  FormFile,
  Button,
  ChecksSvg,
  CheckSvg,
  XSvg,
  BusinessSelector,
  AssetTypeSelector,
  AssetPurchaseTypeSelector,
  FileItem,
  Modal,
  FormText,
  Dropdown,
} from "@components/index";
import AlertImg from "@assets/images/alert-triangle.svg";

export const AssetForm = ({
  isUpdating = false,
  initialBusinessId,
  asset,
  excludeTypes,
  onClose,
  onCreated,
}: {
  isUpdating?: boolean;
  initialBusinessId?: string;
  asset?: AssetModel;
  excludeTypes?: AssetType[];
  onClose: () => void;
  onCreated?: (assetId: string) => void;
}) => {
  const notify = useNotify();
  const setState = useCommonStore(({ setState }) => setState);
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });
  const queryClient = useQueryClient();

  const businesses = useUserStore(({ businesses }) => businesses);
  const defaultBusiness = useUserStore(({ business }) => business);
  const [businessId, setBusinessId] = useState("");

  const [date, setDate] = useState<Date>();
  const [firstLetAt, setFirstLetAt] = useState<Date>();

  const [typeId, setTypeId] = useState<AssetType | "">("");
  const [purchaseTypeId, setPurchaseTypeId] = useState<AssetPurchaseType | "">(
    "",
  );

  const [joint, setJoint] = useState<AssetJoint>("no");

  const [disabledAgreements, setDisabledAgreements] = useState(false);
  const [filesAgreement, setFilesAgreement] = useState<FileModel[]>([]);
  const [isUploadingAgreement, setIsUploadingAgreement] = useState(false);
  const [filesAgreementError, setFilesAgreementError] = useState("");
  const [disabledInvoices, setDisabledInvoices] = useState(false);
  const [filesInvoice, setFilesInvoice] = useState<FileModel[]>([]);
  const [isUploadingInvoice, setIsUploadingInvoice] = useState(false);
  const [filesInvoiceError, setFilesInvoiceError] = useState("");

  const [attachments, setAttachments] = useState<AssetAttachmentModel[]>([]);

  const [modalLess300, setModalLess300] = useState(false);

  const [uploadLater, setUploadLater] = useState(false);

  const labelNameField = useMemo(
    () =>
      typeId === "vehicle"
        ? "Vehicle Registration"
        : typeId === "rental_property"
          ? "First Address Line"
          : "Description",
    [typeId],
  );

  const labelDateField = useMemo(
    () => (purchaseTypeId === "lease" ? "Lease Date" : "Purchase Date"),
    [purchaseTypeId],
  );

  const showFirstLetAtField = useMemo(
    () => typeId === "rental_property",
    [typeId],
  );

  const showCostField = useMemo(
    () => purchaseTypeId !== "lease",
    [purchaseTypeId],
  );

  const showJointField = useMemo(() => typeId === "rental_property", [typeId]);

  const showJointShareField = useMemo(() => joint === "yes", [joint]);

  const showFileAgreementField = useMemo(
    () =>
      (typeId === "vehicle" &&
        ["hire_purchase", "personal_contract_purchase", "loan"].includes(
          purchaseTypeId,
        )) ||
      (typeId === "rental_property" && purchaseTypeId !== "lease") ||
      (typeId === "other" && purchaseTypeId === "finance"),
    [typeId, purchaseTypeId],
  );

  const labelFileAgreementField = useMemo(
    () => getLabelFileAgreementField(typeId, purchaseTypeId),
    [typeId, purchaseTypeId],
  );

  const showAgreementCheckboxes = useMemo(
    () =>
      !filesAgreement.length &&
      !attachments.filter(({ type }) => type === "loan_agreement").length,
    [filesAgreement, attachments],
  );

  const showFileInvoiceField = useMemo(
    () =>
      (typeId === "vehicle" && purchaseTypeId !== "lease") ||
      typeId === "other",
    [typeId, purchaseTypeId],
  );

  const showInvoiceCheckboxes = useMemo(
    () =>
      !filesInvoice.length &&
      !attachments.filter(({ type }) => type === "invoice").length,
    [filesInvoice, attachments],
  );

  const { mutate: mutateCreateAsset, isPending: isPendingCreateAsset } =
    useAssetCreate(
      (assetId) => {
        reset();
        notify("success", "Asset created successfully.");
        handleClose();
        setState({ isLoading: false });
        queryClient.invalidateQueries({
          queryKey: ["assetGetBusinessAssets", businessId],
        });
        if (onCreated) {
          onCreated(assetId);
        }
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(
            key as
              | "businessId"
              | "type"
              | "purchaseType"
              | "name"
              | "date"
              | "firstLetAt"
              | "cost"
              | "jointShare"
              | "comment",
            {
              message: fields![key],
            },
          ),
        );
        notify("error", message);
      },
    );

  const { mutate: mutateUpdateAsset, isPending: isPendingUpdateAsset } =
    useAssetUpdate(
      () => {
        reset();
        notify("success", "Asset updated successfully.");
        handleClose();
        setState({ isLoading: false });
        queryClient.invalidateQueries({
          queryKey: ["assetGetBusinessAssets", businessId],
        });
      },
      ({ message, fields }) => {
        Object.keys(fields || {}).forEach((key) =>
          setError(
            key as
              | "businessId"
              | "type"
              | "purchaseType"
              | "name"
              | "date"
              | "firstLetAt"
              | "cost"
              | "jointShare"
              | "comment",
            {
              message: fields![key],
            },
          ),
        );
        notify("error", message);
      },
    );

  useEffect(() => {
    setState({ isLoading: isPendingCreateAsset || isPendingUpdateAsset });
  }, [isPendingCreateAsset, isPendingUpdateAsset, setState]);

  const {
    register,
    handleSubmit,
    formState,
    reset,
    clearErrors,
    setError,
    setValue,
    getValues,
  } = useForm<AssetFormInterface>({
    mode: "onBlur",
    disabled: isPendingCreateAsset || isPendingUpdateAsset,
    resolver: yupResolver(assetSchema),
  });

  useEffect(() => {
    if (isUpdating && asset) {
      setBusinessId(asset.userBusiness!.uniqueId);
      setTypeId(asset.type);
      setPurchaseTypeId(asset.purchaseType);
      setDate(new Date(asset.date));
      setFirstLetAt(asset.firstLetAt ? new Date(asset.firstLetAt) : undefined);
      setJoint(asset.jointShare ? "yes" : "no");
      setDisabledAgreements(!!asset.notApplicableAgreement);
      setDisabledInvoices(!!asset.notApplicableInvoice);
      reset({
        businessId: asset.userBusiness!.uniqueId,
        businessType: businesses.find(
          ({ uniqueId }) => uniqueId === asset.userBusiness!.uniqueId,
        )?.type,
        type: asset.type,
        purchaseType: asset.purchaseType,
        name: asset.name,
        date: new Date(asset.date),
        firstLetAt: asset.firstLetAt ? new Date(asset.firstLetAt) : undefined,
        cost: asset.cost ? formatAmountAfterEditing(asset.cost) : undefined,
        joint: asset.jointShare ? "yes" : "no",
        jointShare: asset.jointShare ? `${asset.jointShare}%` : undefined,
        comment: asset.comment || "",
        notApplicableAgreement: asset.notApplicableAgreement,
        uploadLaterAgreement: asset.uploadLaterAgreement,
        notApplicableInvoice: asset.notApplicableInvoice,
        uploadLaterInvoice: asset.uploadLaterInvoice,
        isDefault: asset.isDefault,
      });
      setAttachments(asset.attachments || []);
    } else {
      const businessId =
        initialBusinessId ||
        defaultBusiness?.uniqueId ||
        businesses[0]?.uniqueId;
      setBusinessId(businessId);
      reset({
        businessId,
        businessType: businesses.find(({ uniqueId }) => uniqueId === businessId)
          ?.type,
      });
    }
  }, [
    isUpdating,
    asset,
    initialBusinessId,
    defaultBusiness,
    businesses,
    reset,
  ]);

  const handleSelectBusiness = (businessId: string) => {
    setBusinessId(businessId);
    setValue("businessId", businessId);
    setValue(
      "businessType",
      businesses.find(({ uniqueId }) => uniqueId === businessId)?.type,
    );
    setTypeId("");
    setValue("type", "");
    setPurchaseTypeId("");
    setValue("purchaseType", "");
    setJoint("no");
    setValue("joint", "no");
    setFilesAgreement([]);
    setFilesInvoice([]);
    setAttachments([]);
    setFilesAgreementError("");
    setFilesInvoiceError("");
  };

  const handleSelectType = (typeId: string) => {
    setTypeId(typeId as AssetType);
    setValue("type", typeId as AssetType);
    clearErrors("type");
    setPurchaseTypeId("");
    setValue("purchaseType", "");
    setJoint("no");
    setValue("joint", "no");
    setFilesAgreement([]);
    setFilesInvoice([]);
    setAttachments([]);
    setFilesAgreementError("");
    setFilesInvoiceError("");
  };

  const handleSelectPurchaseType = (purchaseTypeId: string) => {
    setPurchaseTypeId(purchaseTypeId as AssetPurchaseType);
    setValue("purchaseType", purchaseTypeId as AssetPurchaseType);
    clearErrors("purchaseType");
    setJoint("no");
    setValue("joint", "no");
    setFilesAgreement([]);
    setFilesInvoice([]);
    setAttachments([]);
    setFilesAgreementError("");
    setFilesInvoiceError("");
  };

  const handleSelectDate = (value?: Date) => {
    setDate(value);
    setValue("date", value || new Date());
    clearErrors("date");
  };

  const handleSelectFirstLetAt = (value?: Date) => {
    setFirstLetAt(value);
    setValue("firstLetAt", value);
    clearErrors("firstLetAt");
  };

  const handleSelectJoint = (value: string) => {
    setJoint(value as AssetJoint);
    setValue("joint", value as AssetJoint);
    clearErrors("joint");
  };

  const handleSelectFilesAgreement = async (target: HTMLInputElement) => {
    setIsUploadingAgreement(true);
    if (!target.files?.length) {
      setIsUploadingAgreement(false);
      return;
    }

    const newFiles = await attachmentsService.getAttachments(target.files);

    if (typeof newFiles === "string") {
      notify("error", newFiles);
      setIsUploadingAgreement(false);
      return;
    }

    const newFilesWithSizes: FileModel[] = newFiles.map((file, index) => ({
      ...file,
      size: target.files?.[index].size,
    }));
    setFilesAgreement([...filesAgreement, ...newFilesWithSizes]);
    clearErrors("filesAgreement");
    setFilesAgreementError("");
    setIsUploadingAgreement(false);
  };

  const handleSelectFilesInvoice = async (target: HTMLInputElement) => {
    setIsUploadingInvoice(true);
    if (!target.files?.length) {
      setIsUploadingInvoice(false);
      return;
    }

    const newFiles = await attachmentsService.getAttachments(target.files);

    if (typeof newFiles === "string") {
      notify("error", newFiles);
      setIsUploadingInvoice(false);
      return;
    }

    const newFilesWithSizes: FileModel[] = newFiles.map((file, index) => ({
      ...file,
      size: target.files?.[index].size,
    }));
    setFilesInvoice([...filesInvoice, ...newFilesWithSizes]);
    clearErrors("filesInvoice");
    setFilesInvoiceError("");
    setIsUploadingInvoice(false);
  };

  const removeFileAgreement = (uuid: string) => {
    const newFiles = filesAgreement.filter((file) => file.uuid !== uuid);
    setFilesAgreement(newFiles);
  };

  const removeFileInvoice = (uuid: string) => {
    const newFiles = filesInvoice.filter((file) => file.uuid !== uuid);
    setFilesInvoice(newFiles);
  };

  const removeAttachment = (uniqueId: string) => {
    const newAttachments = attachments.filter(
      (attachment) => attachment.uniqueId !== uniqueId,
    );
    setAttachments(newAttachments);
  };

  const handleChangeNoDocumentationAgreement = () => {
    clearErrors("notApplicableAgreement");
    setFilesAgreementError("");
    setDisabledAgreements(!getValues("notApplicableAgreement"));
    if (!getValues("notApplicableAgreement")) {
      setValue("uploadLaterAgreement", false);
      setFilesAgreement([]);
      setAttachments(
        attachments.filter(({ type }) => type !== "loan_agreement"),
      );
    }
  };

  const handleChangeNoDocumentationInvoice = () => {
    clearErrors("notApplicableInvoice");
    setFilesInvoiceError("");
    setDisabledInvoices(!getValues("notApplicableInvoice"));
    if (!getValues("notApplicableInvoice")) {
      setValue("uploadLaterInvoice", false);
      setFilesInvoice([]);
      setAttachments(attachments.filter(({ type }) => type !== "invoice"));
    }
  };

  const handleChangekUploadLaterAgreement = () => {
    clearErrors("uploadLaterAgreement");
    setFilesAgreementError("");
    setUploadLater(!getValues("uploadLaterAgreement"));
    if (!getValues("uploadLaterAgreement")) {
      setValue("notApplicableAgreement", false);
      setDisabledAgreements(false);
      setFilesAgreement([]);
      setAttachments(
        attachments.filter(({ type }) => type !== "loan_agreement"),
      );
    }
  };

  const handleChangeUploadLaterInvoice = () => {
    clearErrors("uploadLaterInvoice");
    setFilesInvoiceError("");
    setUploadLater(!getValues("uploadLaterInvoice"));
    if (!getValues("uploadLaterInvoice")) {
      setValue("notApplicableInvoice", false);
      setDisabledInvoices(false);
      setFilesInvoice([]);
      setAttachments(attachments.filter(({ type }) => type !== "invoice"));
    }
  };

  const onSubmit: SubmitHandler<AssetFormInterface> = (data) => {
    const isFilesAgreementError =
      showFileAgreementField &&
      !getValues("notApplicableAgreement") &&
      !getValues("uploadLaterAgreement") &&
      !attachments.filter(({ type }) => type === "loan_agreement").length &&
      !filesAgreement.length;
    const isFilesInvoiceError =
      showFileInvoiceField &&
      !getValues("notApplicableInvoice") &&
      !getValues("uploadLaterInvoice") &&
      !attachments.filter(({ type }) => type === "invoice").length &&
      !filesInvoice.length;
    setFilesAgreementError(
      !isFilesAgreementError
        ? ""
        : "You have to upload your documents or set one of checkboxes above.",
    );
    setFilesInvoiceError(
      !isFilesInvoiceError
        ? ""
        : "You have to upload your documents or set one of checkboxes above.",
    );
    if (isFilesAgreementError || isFilesInvoiceError) {
      return;
    }

    const cost = data.cost?.replaceAll(/[^0-9/.]*/g, "");
    const jointShare = data.jointShare?.replaceAll(/[^0-9]*/g, "");
    const firstLetAt = data.firstLetAt ? new Date(data.firstLetAt) : undefined;
    if (
      data.type !== "rental_property" &&
      data.purchaseType !== "lease" &&
      !!cost &&
      parseFloat(cost) < 300
    ) {
      setModalLess300(true);
      return;
    }

    const assetFilesAgreement: AssetFileModel[] = filesAgreement.map(
      (file) => ({
        ...file,
        type: "loan_agreement" as AssetFileType,
      }),
    );
    const assetFilesInvoice: AssetFileModel[] = filesInvoice.map((file) => ({
      ...file,
      type: "invoice" as AssetFileType,
    }));

    return !isUpdating
      ? mutateCreateAsset({
          ...data,
          attachments,
          filesAgreement: assetFilesAgreement,
          filesInvoice: assetFilesInvoice,
          cost,
          jointShare: joint === "yes" ? jointShare : undefined,
          firstLetAt,
        })
      : mutateUpdateAsset({
          ...data,
          assetId: asset?.uniqueId,
          attachments,
          filesAgreement: assetFilesAgreement,
          filesInvoice: assetFilesInvoice,
          cost,
          jointShare: joint === "yes" ? jointShare : undefined,
          firstLetAt,
        });
  };

  const handleClose = () => {
    setState({ isDrawerShown: false });
    onClose();
  };

  return (
    <Drawer className="drawer-asset-form">
      <DrawerHeader
        onClose={handleClose}
        title={
          <div className="text-300 font-bold text-black-100 md:text-600">
            {!isUpdating ? "Add" : "Edit"} Asset
          </div>
        }
      />
      <Form
        className="max-h-[calc(100dvh_-_50px)] overflow-y-auto px-4 md:max-h-[calc(100dvh_-_104px)] md:px-14"
        onSubmit={handleSubmit(onSubmit)}
      >
        <input
          {...register<"isDefault">("isDefault")}
          type="hidden"
          name="isDefault"
        />
        <FormRow top={isMobile ? "small" : "large"}>
          <BusinessSelector
            value={businessId}
            onSetBusiness={handleSelectBusiness}
            variant="formField"
            dataTestid="input_businessId"
          />
          <input
            {...register<"businessId">("businessId")}
            type="hidden"
            name="businessId"
          />
          <input
            {...register<"businessType">("businessType")}
            type="hidden"
            name="businessType"
          />
        </FormRow>
        <FormRow top="small">
          <AssetTypeSelector
            businessId={businessId}
            excludeTypes={excludeTypes}
            error={formState.errors.type?.message}
            value={typeId}
            dataTestid="input_assetType"
            onSetAssetType={handleSelectType}
          />
          <input {...register<"type">("type")} type="hidden" name="type" />
        </FormRow>
        <FormRow
          top={isMobile ? "large" : "medium"}
          className="text-300 font-bold md:text-400"
        >
          Asset information
        </FormRow>
        <FormRow top={"small"}>
          <FormInput
            {...register<"name">("name")}
            name="name"
            label={labelNameField}
            error={formState.errors.name?.message}
            data-testid="input_registrationNumber"
            onBlur={({ currentTarget }) =>
              setValue("name", currentTarget.value.trim())
            }
            onChange={() => clearErrors("name")}
          />
        </FormRow>
        <FormRow top="small">
          <AssetPurchaseTypeSelector
            type={typeId}
            error={formState.errors.purchaseType?.message}
            value={purchaseTypeId}
            onSetAssetPurchaseType={handleSelectPurchaseType}
            dataTestid="input_purchaseType"
          />
          <input
            {...register<"purchaseType">("purchaseType")}
            type="hidden"
            name="purchaseType"
          />
        </FormRow>
        <FormRow top="small" className="field-asset-date">
          <DatePicker
            parent=".field-asset-date"
            date={date}
            prefix="assetForm"
            label={labelDateField}
            dataTestid="input_purchaseDate"
            onSetDate={handleSelectDate}
            error={
              formState.errors.date?.message && "The Date field is required"
            }
          />
          <input {...register<"date">("date")} type="hidden" name="date" />
        </FormRow>
        {showFirstLetAtField && (
          <FormRow top="small" className="field-asset-firstLetAt">
            <DatePicker
              parent=".field-asset-firstLetAt"
              date={firstLetAt}
              label="Date first let"
              onSetDate={handleSelectFirstLetAt}
              error={formState.errors.firstLetAt?.message}
            />
            <input
              {...register<"firstLetAt">("firstLetAt")}
              type="hidden"
              name="firstLetAt"
            />
          </FormRow>
        )}
        {showCostField && (
          <FormRow top={"small"}>
            <FormInput
              {...register<"cost">("cost")}
              name="cost"
              label="Cost"
              maxLength={13}
              error={formState.errors.cost?.message}
              onChange={({ currentTarget }) => {
                setValue("cost", currentTarget.value);
                clearErrors("cost");
              }}
              onBlur={({ currentTarget }) =>
                setValue("cost", formatAmountOnBlur(currentTarget.value))
              }
            />
          </FormRow>
        )}
        {showJointField && (
          <FormRow top={"small"}>
            <Dropdown
              items={[
                { id: "no", title: "No" },
                { id: "yes", title: "Yes" },
              ]}
              label="Joint"
              value={joint}
              onSelect={handleSelectJoint}
              error={formState.errors.joint?.message}
            />
            <input {...register<"joint">("joint")} type="hidden" name="joint" />
          </FormRow>
        )}
        {showJointShareField && (
          <FormRow top={"small"}>
            <FormInput
              {...register<"jointShare">("jointShare")}
              name="jointShare"
              label="Share"
              maxLength={4}
              error={formState.errors.jointShare?.message}
              onChange={({ currentTarget }) => {
                setValue(
                  "jointShare",
                  formatPercentOnEdit(currentTarget.value),
                );
                clearErrors("jointShare");
              }}
              onBlur={({ currentTarget }) =>
                setValue("jointShare", formatPercentOnBlur(currentTarget.value))
              }
            />
          </FormRow>
        )}
        <FormRow top={"small"}>
          <FormText
            {...register<"comment">("comment")}
            name="comment"
            label="Comment"
            error={formState.errors.comment?.message}
            data-testid="input_comment"
            onChange={() => clearErrors("comment")}
          />
        </FormRow>
        {showFileAgreementField && (
          <>
            <FormRow
              top={isMobile ? "large" : "medium"}
              className="text-300 font-bold md:text-400"
            >
              {labelFileAgreementField}
            </FormRow>
            {showAgreementCheckboxes && (
              <>
                <FormRow top="small">
                  <FormCheckbox
                    {...register<"notApplicableAgreement">(
                      "notApplicableAgreement",
                    )}
                    name="notApplicableAgreement"
                    label="No documentation available"
                    error={formState.errors.notApplicableAgreement?.message}
                    onChange={handleChangeNoDocumentationAgreement}
                  />
                </FormRow>
                <FormRow top="small">
                  <FormCheckbox
                    {...register<"uploadLaterAgreement">(
                      "uploadLaterAgreement",
                    )}
                    name="uploadLaterAgreement"
                    label="Upload later"
                    error={formState.errors.uploadLaterAgreement?.message}
                    onChange={handleChangekUploadLaterAgreement}
                  />
                </FormRow>
              </>
            )}
            <FormRow top={"small"}>
              <FormFile
                {...register<"filesAgreement">("filesAgreement")}
                name="filesAgreement"
                error={filesAgreementError}
                accept={ASSET_FILE_EXTENSIONS}
                uploading={isUploadingAgreement}
                disabled={disabledAgreements}
                dataTestid="input_uploadFiles"
                onChange={(event) => {
                  handleSelectFilesAgreement(event.target as HTMLInputElement);
                  event.preventDefault();
                }}
              />
            </FormRow>
            {!attachments.filter(({ type }) => type === "loan_agreement")
              .length ? null : (
              <div className="mt-4 flex flex-col gap-y-4">
                {attachments
                  .filter(({ type }) => type === "loan_agreement")
                  .map((attachment) => (
                    <FileItem
                      key={attachment.uniqueId}
                      file={attachment}
                      onRemove={removeAttachment}
                    />
                  ))}
              </div>
            )}
            {!filesAgreement.length ? null : (
              <div className="mt-4 flex flex-col gap-y-4">
                {filesAgreement.map((file) => (
                  <FileItem
                    key={file.uuid}
                    file={file}
                    onRemove={removeFileAgreement}
                  />
                ))}
              </div>
            )}
          </>
        )}
        {showFileInvoiceField && (
          <>
            <FormRow
              top={isMobile ? "large" : "medium"}
              className="text-300 font-bold md:text-400"
            >
              Invoice
            </FormRow>
            {showInvoiceCheckboxes && (
              <>
                <FormRow top="small">
                  <FormCheckbox
                    {...register<"notApplicableInvoice">(
                      "notApplicableInvoice",
                    )}
                    name="notApplicableInvoice"
                    label="No documentation available"
                    error={formState.errors.notApplicableInvoice?.message}
                    onChange={handleChangeNoDocumentationInvoice}
                  />
                </FormRow>
                <FormRow top="small">
                  <FormCheckbox
                    {...register<"uploadLaterInvoice">("uploadLaterInvoice")}
                    name="uploadLaterInvoice"
                    label="Upload later"
                    error={formState.errors.uploadLaterInvoice?.message}
                    onChange={handleChangeUploadLaterInvoice}
                  />
                </FormRow>
              </>
            )}
            <FormRow top={"small"}>
              <FormFile
                {...register<"filesInvoice">("filesInvoice")}
                name="filesInvoice"
                error={filesInvoiceError}
                accept={ASSET_FILE_EXTENSIONS}
                uploading={isUploadingInvoice}
                disabled={disabledInvoices}
                dataTestid="input_uploadFiles"
                onChange={(event) => {
                  handleSelectFilesInvoice(event.target as HTMLInputElement);
                  event.preventDefault();
                }}
              />
            </FormRow>
            {!attachments.filter(({ type }) => type === "invoice")
              .length ? null : (
              <div className="mt-4 flex flex-col gap-y-4">
                {attachments
                  .filter(({ type }) => type === "invoice")
                  .map((attachment) => (
                    <FileItem
                      key={attachment.uniqueId}
                      file={attachment}
                      onRemove={removeAttachment}
                    />
                  ))}
              </div>
            )}
            {!filesInvoice.length ? null : (
              <div className="mt-4 flex flex-col gap-y-4">
                {filesInvoice.map((file) => (
                  <FileItem
                    key={file.uuid}
                    file={file}
                    onRemove={removeFileInvoice}
                  />
                ))}
              </div>
            )}
          </>
        )}
        {!isMobile && (
          <div className="-mx-10 mt-10 h-px w-[calc(100%_+_80px)] bg-black-8" />
        )}
        <FormRow
          isRow={!isMobile}
          isBetween
          top="small"
          className="gap-y-4 pb-6"
        >
          <div
            className="flex flex-col gap-4 md:flex-row"
            data-testid="button_saveAsset"
          >
            <Button
              type="submit"
              title={`${!isUpdating ? "Add" : "Edit"} Asset`}
              leftIcon={<ChecksSvg />}
              className="w-full md:w-fit md:px-5"
              height="small"
              onClick={() => setValue("isDefault", false)}
            />
            <Button
              type="submit"
              variant="secondary"
              title={
                asset?.isDefault
                  ? "Save as Not default Asset"
                  : "Save as Default Asset"
              }
              leftIcon={<CheckSvg />}
              className="w-full md:w-fit md:px-5"
              height="small"
              onClick={() =>
                setValue("isDefault", isUpdating ? !asset?.isDefault : true)
              }
            />
          </div>
          <Button
            title="Cancel"
            variant="secondary"
            height="small"
            leftIcon={<XSvg />}
            onClick={handleClose}
          />
        </FormRow>
      </Form>
      <Modal show={modalLess300} onClose={() => setModalLess300(false)}>
        <div className="flex flex-col items-center text-center">
          <img
            src={AlertImg}
            alt="Alert"
            className="h-12 w-12 md:h-14 md:w-14"
          />
          <div className="pt-1 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            We would not normally capitalise any assets less than £300. We would
            instead advise you to add this as a transaction to the most relevant
            category.
          </div>
        </div>
        <div className="flex justify-between gap-x-2 pt-8 md:gap-x-8 md:pt-12">
          <Button
            type="button"
            variant="primary"
            title="Ok"
            className="mx-auto w-40"
            onClick={() => setModalLess300(false)}
          />
        </div>
      </Modal>
      <Modal show={uploadLater} onClose={() => setUploadLater(false)}>
        <div className="flex flex-col items-center text-center">
          <img
            src={AlertImg}
            alt="Alert"
            className="h-12 w-12 md:h-14 md:w-14"
          />
          <div className="pt-1 text-100 text-black-54 md:text-300 md:font-light md:text-black-70">
            Please come back to add documents before your next submission.
          </div>
        </div>
        <div className="flex justify-between gap-x-2 pt-8 md:gap-x-8 md:pt-12">
          <Button
            type="button"
            variant="primary"
            title="Ok"
            className="mx-auto w-40"
            onClick={() => setUploadLater(false)}
          />
        </div>
      </Modal>
    </Drawer>
  );
};
